<template>
  <div class="h-full audifonts__audi-type">
    <div
      v-if="(loading.event || loading.token) && !config.template.header"
      class="text-center py-10 flex items-center justify-center min-h-screen"
    >
      <XSpinner class="text-4xl text-info-50" />
    </div>
    <div v-else>
      <div
        v-if="unpublish"
        class="font-medium text-black text-center py-4"
      >
        {{ $t('event.registration.unpublish') }}
      </div>

      <div
        class="h-full bg-center bg-cover"
        :style="baseStyles"
      >
        <div
          v-if="privateRegistration && !tokenIsValid"
          class="h-full py-8 flex items-center justify-center font-medium"
        >
          {{ $t('event.registration.invalidToken') }}
        </div>
        <div v-else>
          <div
            v-if="config.template.header.adjust"
            class="relative flex items-center justify-center"
            :style="headerStyles"
          >
            <img
              :class="$style['header-image']"
              :src="config.template.header.background.imageUrl"
            >

            <div
              class="absolute inline-block mx-auto flex items-center justify-center"
              :class="$style['logo']"
            >
              <img
                class="block mx-w-full h-full mx-auto"
                :style="config.template.header.logo.styles"
                :src="config.template.header.logo.imageUrl"
              >
            </div>
          </div>

          <div
            v-else
            class="relative flex bg-center bg-cover py-15"
            :style="headerStyles"
          >
            <div class="max-w-md mx-auto mb-8">
              <img
                :src="config.template.header.logo.imageUrl"
                class="block mx-w-full h-auto mx-auto"
                width="170"
                alt="Logo"
              >
            </div>
          </div>

          <div :style="config.template.body.styles">
            <div
              v-if="config.template.lang"
              class="text-center pt-8 text-lg"
            >
              <a
                v-for="(messages, locale) in config.template.lang"
                :href="'?' + locale.split('_').shift()"
                :key="locale"
                class="py-2 px-3 font-normal whitespace-no-wrap border-0 text-grey-40 cursor-pointer rounded-sm no-underline"
                :class="{ 'hover:bg-grey-95': $i18n.locale !== locale, 'border-b font-semibold': $i18n.locale === locale }"
              >
                <span>{{ $t('common.languages.' + locale) }}</span>
              </a>
            </div>

            <router-view
              :eventId="eventId"
              :eventSlug="eventSlug"
              :formFields="formFields"
              :config="pages"
              :redirectToTimeline="redirectToTimeline"
              :token="token"
              :lang="currentLang"
              :loading="loading.template"
              :ticketTypes="ticketTypes"
              :ticketPaymentIntent="ticketPaymentIntent"
              @attendeeCreated="fetchTemplate"
            />
          </div>

          <div
            class="sm:mx-6 px-3 pb-3 sm:px-8 sm:pb-8"
            :class="$style['terms']"
            :style="footer.styles"
          >
            <div v-html="footer.content" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { mapState } from 'vuex'

import _ from 'lodash'

export default {
  data () {
    return {
      event: {},
      unpublish: false,
      loading: {
        event: true,
        template: false,
        token: false
      }
    }
  },

  computed: {
    ...mapState({
      eventId: state => state.routeParameters.event.id,
      eventSlug: state => state.routeParameters.event.slug,
      token: state => state.routeParameters.token
    }),

    locale () {
      return this.$i18n.locale
    },

    config () {
      return {
        config: this.event.registrationConfiguration || {},
        template: this.event.registrationTemplate || {}
      }
    },

    redirectToTimeline () {
      return this.config.config.redirectToTimeline
    },

    formFields () {
      return this.event.formFields
    },

    ticketPaymentIntent () {
      return this.event.ticketPaymentIntent
    },

    ticketTypes () {
      return this.event.ticketTypes
    },

    privateRegistration () {
      return this.event.privateRegistration
    },

    baseStyles () {
      return {
        backgroundImage: this.config.template.imageUrl ? 'url(' + this.config.template.imageUrl + ')' : '',
        ...this.config.template.styles
      }
    },

    headerStyles () {
      if (this.config.template.header.adjust) {
        return this.config.template.header.background.styles
      }

      return {
        backgroundImage: 'url(' + this.config.template.header.background.imageUrl + ')',
        ...this.config.template.header.background.styles
      }
    },

    currentLang () {
      if (this.config.template.lang) {
        return this.config.template.lang[this.locale]
      } else {
        return null
      }
    },

    pages () {
      let pages = _.cloneDeep(this.config.template.body.pages)

      _.forEach(pages, (page, key) => {
        pages[key].content = pages[key].content.replace(/#_#.*?#_#/g, str => {
          let contentKey = str.replace(/#_#/g, '')

          return this.currentLang[contentKey] || contentKey
        })

        if (key === 'form') {
          pages.form.formFields.submit.content = pages.form.formFields.submit.content.replace(/#_#.*?#_#/g, str => {
            let contentKey = str.replace(/#_#/g, '')

            return this.currentLang[contentKey] || contentKey
          })
        }
      })

      return pages
    },

    footer () {
      if (this.config.template.lang && this.currentLang) {
        let footer = _.cloneDeep(this.config.template.footer)

        footer.content = footer.content.replace(/#_#.*?#_#/g, str => {
          let contentKey = str.replace(/#_#/g, '')

          return this.currentLang[contentKey] || contentKey
        })

        return footer
      }

      return this.config.template.footer
    }
  },

  beforeRouteEnter (to, from, next) {
    next(vm => {
      vm.fetchEvent()

      if (window.location.search === '?en') {
        vm.changeLang('en_GB')
      }
    })
  },

  beforeRouteUpdate (to, from, next) {
    if (to.name === 'registrationForm') {
      this.event = {}
      this.fetchEvent()
    }

    next()
  },

  methods: {
    routeResolver (isRegistrationExpired, isPrivateRegistration, isRegistrationFullCapacity) {
      if (isRegistrationExpired) {
        return this.$router.replace('closed')
      }

      if (isRegistrationFullCapacity) {
        return this.$router.replace('fullcapacity')
      }

      if (isPrivateRegistration) {
        this.fetchTokenValidation.start()
      }

      return this.$router.replace('/')
    },

    changeLang (locale) {
      let message = require('@plat/lang/' + locale).default

      this.$i18n.locale = locale
      this.$i18n.setLocaleMessage(locale, message)
    },

    fetchTokenValidation () {
      this.loading.token = true

      this.$apollo
        .query({
          query: gql`query Event ($eventId: Uuid!, , $token: String!) {
            event (eventId: $eventId) {
              validateRegistrationToken(token: $token)
            }
          }`,
          variables: {
            eventId: this.eventId,
            token: this.token
          }
        })
        .then(result => {
          this.tokenIsValid = result.data.event.validateRegistrationToken
        })
        .finally(() => {
          this.loading.token = false
        })
    },

    fetchTemplate (attendeeId) {
      this.loading.template = true

      this.$apollo
        .query({
          query: gql`query Event ($eventId: Uuid!, $attendeeId: Uuid) {
            event (eventId: $eventId) {
              registrationTemplate (attendeeId: $attendeeId) {
                imageUrl
                styles
                header
                body
                footer
                lang
              }
            }
          }`,
          fetchPolicy: 'network-only',
          variables: {
            eventId: this.eventId,
            attendeeId: attendeeId
          }
        })
        .then(result => {
          this.event.registrationTemplate = result.data.event.registrationTemplate
        })
        .finally(() => {
          this.loading.template = false
        })
    },

    fetchEvent () {
      this.loading.event = true

      this.$apollo
        .query({
          query: gql`query Event ($eventId: Uuid!) {
            event (eventId: $eventId) {
              isRegistrationExpired
              isRegistrationFullCapacity
              isRegistrationPublished
              privateRegistration
              ticketTypes {
                ticket
                label
              }
              registrationTemplate {
                imageUrl
                styles
                header
                body
                footer
                lang
              }
              registrationConfiguration {
                redirectToTimeline
              }
              formFields {
                type
                required
                web
                config {
                  id
                  type
                  label
                  url
                  options
                  rules
                }
              }
              ticketPaymentIntent {
                price
                paymentId
                stripeKey
                stripeClientSecret
              }
            }
          }`,
          variables: {
            eventId: this.eventId
          },
          fetchPolicy: 'network-only'
        })
        .then(result => {
          this.event = {
            ...result.data.event
          }

          this.unpublish = !this.event.isRegistrationPublished

          this.routeResolver(this.event.isRegistrationExpired, this.event.privateRegistration, this.event.isRegistrationFullCapacity)
        })
        .finally(() => {
          this.loading.event = false
        })
    }
  }
}
</script>

<style
  lang="scss"
  module
>
.header-image {
  display: block;
  max-width: 100%;
  max-height: 400px;
  margin: 0 auto;
}

.terms {
  p {
    line-height: 1;
  }
}

.logo {
  height: calc(100% - 20px);
  left: 50%;
  transform: translateX(-50%);
}
</style>
